import {useMemo, useCallback, useState} from 'react'
import {Select, MenuItem, Box, type SelectChangeEvent} from '@mui/material'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import TuneIcon from '@mui/icons-material/Tune'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {selectActiveScene, CUSTOM_DEVICE_NAME} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useDeviceCatalog} from '../../../common/hooks/useDeviceCatalog'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {DeviceConfig} from '../../../common/components/types'
import {useEditorConfig} from '../../../common/hooks/useEditorConfig'
import {getDevice, trackDeviceUsage} from '../../../common/api/generated/endpoints/devices/devices'
import type {DeviceListItem} from '../../../common/api/generated/model/deviceListItem'
import {trackEvent} from '../../../common/analytics/trackEvent'
import LoadingLogo from '../LoadingLogo'

const CUSTOM_VALUE = '__custom__'

const renderOSIcon = (os: DeviceListItem['os'], sx: ReturnType<typeof buildStyles>) =>
  os === 'apple' ? <AppleIcon sx={sx.osIcon} /> : <AndroidIcon sx={sx.osIcon} />

// ── FreeDeviceSelectPanel — dropdown for free devices only ────────

const FreeDeviceSelectPanel = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const {setDeviceConfig} = useEditorConfig()
  const activeDeviceName = useAppSelector((s) => selectActiveScene(s).activeDeviceName)
  const {devices, loading} = useDeviceCatalog(true)
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null)
  const isCustom = activeDeviceName === CUSTOM_DEVICE_NAME

  const handleChange = useCallback(
    async (e: SelectChangeEvent) => {
      const slug = e.target.value
      if (slug === CUSTOM_VALUE) return
      const device = devices.find((d) => d.slug === slug)
      if (!device || device.name === activeDeviceName) return
      setLoadingSlug(slug)
      try {
        const {data} = await getDevice(slug)
        setDeviceConfig(data.config as unknown as DeviceConfig)
        dispatch(sealUndo())
        trackDeviceUsage(slug).catch(() => {})
        trackEvent('device_preset_selected', {device_slug: slug, device_name: device.name, editor_mode: 'free'})
      } finally {
        setLoadingSlug(null)
      }
    },
    [dispatch, activeDeviceName, devices, setDeviceConfig],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  if (loading) {
    return (
      <Box sx={sx.loader}>
        <LoadingLogo size={24} />
      </Box>
    )
  }

  const activeSlug = isCustom ? CUSTOM_VALUE : (devices.find((d) => d.name === activeDeviceName)?.slug ?? '')

  return (
    <Select
      fullWidth
      size="small"
      value={activeSlug}
      onChange={handleChange}
      disabled={loadingSlug !== null}
      sx={sx.select}
      MenuProps={{slotProps: {paper: {sx: sx.menuPaper}}}}
    >
      {isCustom && (
        <MenuItem key="custom" value={CUSTOM_VALUE} sx={sx.item}>
          <TuneIcon sx={sx.osIcon} />
          <Box component="span" sx={sx.itemText}>
            {t('device.select.custom')}
          </Box>
        </MenuItem>
      )}
      {devices.map((device) => (
        <MenuItem key={device.slug} value={device.slug} sx={sx.item}>
          {renderOSIcon(device.os, sx)}
          <Box component="span" sx={sx.itemText}>
            {device.name}
          </Box>
        </MenuItem>
      ))}
    </Select>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '12px',
  },
  select: {
    fontSize: 13,
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 32px 6px 10px',
    },
  },
  menuPaper: {
    backgroundColor: tokens.sidebar.background,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: 13,
  },
  osIcon: {
    fontSize: 18,
    color: tokens.text.muted,
  },
  itemText: {
    fontSize: 13,
    fontWeight: 500,
  },
})

export default FreeDeviceSelectPanel
